import { useEffect, useMemo, useState } from 'react';
import { hoaService, homeService } from '../clients/ApiClients';
import { HoaReturn } from '../clients/rust-api-client';
import { useUser } from '../hooks/UserContext';
import { UserType } from '../constants/UserTypes';
import { useSyncedHoas, useSyncedHomes } from '../sync/hooks';

/**
 * Which HOAs this person can see, by name, and which HOA each of their
 * homes belongs to — for filtering lists by HOA (#618).
 *
 * Work orders carry `hoa_id`; violations carry only `home_id`, so for them
 * this also loads every visible HOA's homes (`withHomes`). That is one
 * request per HOA, the same data the ⌘K palette loads, and is what a
 * `hoa_id` on `ViolationReturn` would make unnecessary.
 *
 * Read from the sync engine's local copy when there is one (#561), which
 * holds exactly the HOAs and homes this person can see. Otherwise it is
 * fetched, and kept for a couple of minutes per person, so moving between
 * the lists does not refetch it. Inactive HOAs are included: old records still
 * belong to them.
 */
export interface HoaDirectory {
  /** Every HOA this person can see, by name. */
  hoas: HoaReturn[];
  hoaName: (hoaId: string) => string | undefined;
  /** The HOA a home belongs to; undefined until homes have loaded. */
  hoaOfHome: (homeId: string) => string | undefined;
  /** True once everything asked for has loaded (or failed). */
  ready: boolean;
}

interface Loaded {
  hoas: HoaReturn[];
  homeHoa: Map<string, string> | null;
}

const FRESH_FOR_MS = 2 * 60 * 1000;
let cache: { key: string; at: number; value: Promise<Loaded> } | null = null;

const load = (
  userType: string | null,
  companyId: string | null,
  withHomes: boolean
): Promise<Loaded> => {
  const key = `${userType}:${companyId}:${withHomes}`;
  if (cache && cache.key === key && Date.now() - cache.at < FRESH_FOR_MS) {
    return cache.value;
  }
  const value = (async (): Promise<Loaded> => {
    // Same source as the sidebar and the HOAs page.
    const request =
      userType === UserType.SUPER_ADMIN
        ? hoaService.getAllHoas()
        : companyId
          ? hoaService.getHoasByCompany({ companyId })
          : null;
    const hoas = request
      ? [...(await request).data].sort((a, b) => a.name.localeCompare(b.name))
      : [];
    if (!withHomes) return { hoas, homeHoa: null };
    // One HOA's homes failing leaves only its violations unfilterable.
    const perHoa = await Promise.allSettled(
      hoas.map((h) => homeService.getHomesByHoa({ hoaId: h.id }))
    );
    const homeHoa = new Map<string, string>();
    perHoa.forEach((r) => {
      if (r.status === 'fulfilled') {
        r.value.data.forEach((home) => homeHoa.set(home.id, home.hoa_id));
      }
    });
    return { hoas, homeHoa };
  })();
  cache = { key, at: Date.now(), value };
  // A failure is not worth remembering.
  value.catch(() => {
    if (cache?.value === value) cache = null;
  });
  return value;
};

export function useHoaDirectory({
  enabled,
  withHomes,
}: {
  /** Off where the list is already scoped to one HOA. */
  enabled: boolean;
  withHomes: boolean;
}): HoaDirectory {
  const { userType, companyId } = useUser();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [done, setDone] = useState(false);
  const syncedHoas = useSyncedHoas();
  const syncedHomes = useSyncedHomes({});
  const fromStore = syncedHoas !== null;

  useEffect(() => {
    if (!enabled || fromStore) return;
    let live = true;
    setDone(false);
    load(userType, companyId, withHomes)
      .then((value) => live && setLoaded(value))
      .catch((err) => console.error('Error loading the HOA directory:', err))
      .finally(() => live && setDone(true));
    return () => {
      live = false;
    };
  }, [enabled, fromStore, userType, companyId, withHomes]);

  return useMemo(() => {
    if (fromStore) {
      const names = new Map(syncedHoas.map((h) => [h.id, h.name]));
      const homeHoa = new Map((syncedHomes ?? []).map((h) => [h.id, h.hoa_id]));
      return {
        hoas: syncedHoas,
        hoaName: (id: string) => names.get(id),
        hoaOfHome: (homeId: string) => homeHoa.get(homeId),
        ready: true,
      };
    }
    const names = new Map((loaded?.hoas ?? []).map((h) => [h.id, h.name]));
    return {
      hoas: loaded?.hoas ?? [],
      hoaName: (id: string) => names.get(id),
      hoaOfHome: (homeId: string) => loaded?.homeHoa?.get(homeId),
      ready: done,
    };
  }, [fromStore, syncedHoas, syncedHomes, loaded, done]);
}
